// Put a werf on the board — by recognising it, not by typing a code.
//
// ── WHY THIS IS A SEARCH AND NOT A FORM ─────────────────────────────────────────
// Every fact this dialog needs is already in the database: the werf, its customer, its address, its
// Bouwsoft quote and order numbers. Asking Alex to type any of them would be asking him to re-enter
// something we know, and to be the one who gets it wrong. So there is one field, it searches
// everything at once, and the only thing a human supplies is the choice and the reason.
//
// ── WHAT IT REFUSES TO OFFER ────────────────────────────────────────────────────
// Archived werven and quotes still in preparation. Planning hours against work nobody has agreed to
// do is how a calendar fills with jobs that never happen; the server-side query is what enforces it,
// and this list simply shows what comes back.
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as A from '../api/planning.ts';
import { correlationId, keys } from '../lib/mutate.ts';
import { Banner, Spinner } from '../ui/bits.tsx';

export function AddProject({ onClose, onAdded }: { onClose: () => void; onAdded: (id: number) => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [picked, setPicked] = useState<A.ProjectChoice | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TYPING IS NOT A QUERY. Without this every keystroke is a round trip, and on a slow connection the
  // answers arrive out of order and the list flickers between two searches.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 220);
    return () => clearTimeout(t);
  }, [q]);

  const results = useQuery({
    queryKey: ['project-search', debounced],
    queryFn: () => A.searchProjects(debounced),
    staleTime: 30_000,
  });

  const list = useMemo(() => results.data ?? [], [results.data]);

  const add = async () => {
    if (!picked || busy) return;
    setBusy(true); setError(null);
    try {
      await A.admitProject(picked.projectId, reason.trim() || 'toegevoegd aan de planner', correlationId());
      await qc.invalidateQueries({ queryKey: keys.projects });
      await qc.invalidateQueries({ queryKey: ['project-search'] });
      onAdded(picked.projectId);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden />
      <div className="panel" role="dialog" aria-label="Werf toevoegen aan de planner">
        <header className="panel-head">
          <strong>Werf toevoegen</strong>
          <span className="spacer" />
          <button className="btn small" onClick={onClose} aria-label="Sluiten">✕</button>
        </header>

        <div className="panel-body stack">
          <div className="field">
            <label htmlFor="werfzoek">Zoek op straat, klant of nummer</label>
            <input id="werfzoek" value={q} autoFocus placeholder="Burggravenlaan, Eeckhout, 2026084…"
                   onChange={(e) => setQ(e.target.value)} />
          </div>

          {error && <Banner kind="err">{error}</Banner>}
          {results.isPending && <Spinner />}
          {!results.isPending && list.length === 0 && (
            <p className="faint">
              Geen lopende of aanvaarde werf gevonden. Werven die gearchiveerd zijn of waarvan de
              offerte nog in opmaak is, staan hier bewust niet tussen.
            </p>
          )}

          <ul className="pick-list">
            {list.map((p) => {
              // THE SHARED RESOLVER, not a second copy of the rule. See `werfLabel`.
              const l = A.werfLabel({ projectName: p.displayName, projectCode: p.projectCode,
                                      officialNumber: p.officialNumber, numberKind: p.numberKind });
              return (
                <li key={p.projectId}>
                  <button className={`pick${picked?.projectId === p.projectId ? ' on' : ''}`}
                          disabled={p.inPlanner}
                          onClick={() => setPicked(p)}>
                    <span className="pick-main">
                      {/* REGEL 1 IS DE PLEK, REGEL 2 HET NUMMER. Een planner zoekt een werf, geen sleutel. */}
                      <span className="werf-name">{l.name}</span>
                      <span className="faint">
                        {l.code ?? (l.kind === 'internal' ? `Nog geen nummer · ${p.projectCode}` : '')}
                        {p.customerName ? ` · ${p.customerName}` : ''}
                      </span>
                      {p.address && <span className="faint">{p.address}</span>}
                    </span>
                    <span className="pick-side">
                      {p.inPlanner && <span className="badge ro">staat al in de planner</span>}
                      {/* AN UNCERTAIN NAME IS LABELLED, NOT HIDDEN. The werf is still safe to pick —
                          it is identified by its Bouwsoft number, which is a fact — but nobody has
                          confirmed what to CALL it, and a planner should know that before it appears
                          on a board under a name they did not choose. */}
                      {!p.inPlanner && p.confidence === 'review_required' && (
                        <span className="badge conflict" title="Naam nog niet bevestigd">naam nakijken</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {picked && (
            <div className="field">
              <label htmlFor="reden">Waarom voeg je deze werf toe?</label>
              <input id="reden" value={reason} placeholder="bv. uitvoering start volgende maand"
                     onChange={(e) => setReason(e.target.value)} />
              <span className="faint">
                Komt in het beslissingslogboek. Zo is later te zien waarom een werf op de planning kwam.
              </span>
            </div>
          )}
        </div>

        <footer className="panel-foot">
          <button className="btn" onClick={onClose}>Annuleren</button>
          <span className="spacer" />
          <button className="btn primary" disabled={!picked || busy} onClick={() => void add()}>
            {busy ? 'Bezig…'
              : picked ? `${A.werfLabel({ projectName: picked.displayName, projectCode: picked.projectCode, officialNumber: picked.officialNumber, numberKind: picked.numberKind }).name} toevoegen`
              : 'Kies een werf'}
          </button>
        </footer>
      </div>
    </>
  );
}
